import { type FC, type ReactNode, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import TopBarProgress from 'react-topbar-progress-indicator';
import { getCSSVariableValue } from '@/_metronic/assets/ts/_utils';
import ErrorBoundary from '../feedback/ErrorBoundary';
import QueryErrorBoundary from '../feedback/QueryErrorBoundary';
import PageSkeleton from './PageSkeleton';

type SuspensedViewProps = {
  children: ReactNode;
  resetKey?: string;
  fallback?: ReactNode;
};

const defaultFallback = <PageSkeleton />;

const SuspensedView: FC<SuspensedViewProps> = ({ children, resetKey, fallback = defaultFallback }) => {
  const { pathname } = useLocation();
  const suspenseKey = resetKey ?? pathname;
  const baseColor = getCSSVariableValue('--bs-primary');

  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });

  // Keep import failures outside Suspense; query failures reset their queries on retry.
  // Related routes can share a reset key to preserve the content already loaded.
  return (
    <ErrorBoundary>
      <Suspense
        key={suspenseKey}
        fallback={
          <>
            <TopBarProgress />
            {fallback}
          </>
        }
      >
        <QueryErrorBoundary>{children}</QueryErrorBoundary>
      </Suspense>
    </ErrorBoundary>
  );
};

export default SuspensedView;
